import { Trash2 } from "lucide-react";
import { ChevronDown } from "reicon-react";
import { useEffect, useMemo, useState } from "react";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../components/ui/alert-dialog.js";
import { Button } from "../../components/ui/button.js";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "../../components/ui/command.js";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover.js";
import { api } from "../../lib/api.js";
import type { InventoryRow, LookupRow } from "./hub-operation-types.js";

export const PROTECTED_INVENTORY_CATEGORY = "Uncategorized";

type LookupList = "inventoryCategories" | "inventoryBrands";

type DeleteDialogState =
	| { kind: "confirm"; name: string; id: number | string }
	| { kind: "blocked"; name: string; count: number }
	| { kind: "error"; message: string }
	| null;

export function inventoryCategoryLabel(raw: string): string {
	const trimmed = raw.trim();
	return trimmed || PROTECTED_INVENTORY_CATEGORY;
}

type LookupUsageRow = { category?: string; brand?: string };

export function countInventoryLookupUsage(
	inventory: InventoryRow[],
	list: LookupList,
	name: string,
	usageRows?: LookupUsageRow[],
): number {
	const needle = name.trim().toLowerCase();
	if (!needle) return 0;
	const rows: LookupUsageRow[] = usageRows ? [...inventory, ...usageRows] : inventory;
	return rows.filter((row) => {
		if (list === "inventoryCategories") {
			return inventoryCategoryLabel(row.category ?? "").toLowerCase() === needle;
		}
		return String(row.brand ?? "").trim().toLowerCase() === needle;
	}).length;
}

function lookupRowForName(rows: LookupRow[], name: string): LookupRow | undefined {
	const needle = name.trim().toLowerCase();
	return rows.find((row) => row.name.trim().toLowerCase() === needle);
}

function isProtectedLookup(list: LookupList, name: string): boolean {
	return list === "inventoryCategories" && name.trim().toLowerCase() === PROTECTED_INVENTORY_CATEGORY.toLowerCase();
}

export function HubInventoryLookupFieldSelect({
	id,
	name,
	label,
	defaultValue,
	items,
	list,
	lookupRows,
	inventory,
	usageRows,
	onRefresh,
}: {
	id: string;
	name: string;
	label: string;
	defaultValue: string;
	items: string[];
	list: LookupList;
	lookupRows: LookupRow[];
	inventory: InventoryRow[];
	usageRows?: LookupUsageRow[];
	onRefresh: () => Promise<void>;
}) {
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState(defaultValue);
	const [query, setQuery] = useState("");
	const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>(null);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	const display = value.trim() || items[0] || "—";

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return items;
		return items.filter((item) => item.toLowerCase().includes(q));
	}, [items, query]);

	const canCreate = useMemo(() => {
		const q = query.trim();
		if (!q) return false;
		const lower = q.toLowerCase();
		return !items.some((item) => item.toLowerCase() === lower);
	}, [items, query]);

	const pick = (item: string) => {
		setValue(item);
		setOpen(false);
		setQuery("");
	};

	const requestDelete = (optionName: string) => {
		const row = lookupRowForName(lookupRows, optionName);
		if (!row?.id || isProtectedLookup(list, optionName)) return;
		const count = countInventoryLookupUsage(inventory, list, optionName, usageRows);
		if (count > 0) {
			setDeleteDialog({ kind: "blocked", name: optionName, count });
			return;
		}
		setDeleteDialog({ kind: "confirm", name: optionName, id: row.id });
	};

	const confirmDelete = async () => {
		if (!deleteDialog || deleteDialog.kind !== "confirm") return;
		setDeleting(true);
		try {
			await api.deleteHubBook({ list, id: deleteDialog.id });
			if (value.trim().toLowerCase() === deleteDialog.name.trim().toLowerCase()) {
				setValue("");
			}
			setDeleteDialog(null);
			setOpen(false);
			setQuery("");
			await onRefresh();
		} catch (error) {
			const message = error instanceof Error ? error.message : "That option could not be removed.";
			setDeleteDialog({ kind: "error", message });
		} finally {
			setDeleting(false);
		}
	};

	const optionLabel = list === "inventoryCategories" ? "category" : "brand";

	return (
		<>
			<div className="hub-inventory-lookup-field flex min-w-0 flex-col gap-1.5">
				<label id={`${id}-label`} htmlFor={id}>
					{label}
				</label>
				<div className="select hub-field-select hub-creatable-select hub-inventory-lookup-select min-w-0">
				<input type="hidden" name={name} id={id} value={value} />
				<Popover open={open} onOpenChange={setOpen} modal={false}>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="hub-field-select-trigger"
							aria-labelledby={`${id}-label`}
							aria-haspopup="listbox"
							aria-expanded={open}
						>
							<span>{display}</span>
							<ChevronDown size={12} aria-hidden="true" />
						</button>
					</PopoverTrigger>
					<PopoverContent className="hub-inventory-lookup-popover p-0" align="start">
						<Command shouldFilter={false}>
							<CommandInput
								placeholder="Search…"
								value={query}
								onValueChange={setQuery}
								aria-label={`Search ${label}`}
							/>
							<CommandList>
								{canCreate ? (
									<CommandItem
										className="select-item create font-semibold"
										value={`__create__${query.trim()}`}
										onSelect={() => pick(query.trim())}
									>
										{`Add "${query.trim()}"`}
									</CommandItem>
								) : null}
								{filtered.map((item) => {
									const manual = lookupRowForName(lookupRows, item);
									const showDelete = Boolean(manual?.id) && !isProtectedLookup(list, item);
									return (
										<CommandItem
											key={item || "empty"}
											value={item}
											className={[
												"hub-inventory-lookup-option group",
												item === value ? "active" : "",
											].filter(Boolean).join(" ")}
											onSelect={() => pick(item)}
										>
											<span className="min-w-0 flex-1 truncate">{item}</span>
											{showDelete ? (
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="hub-inventory-lookup-delete h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100"
													aria-label={`Delete ${item}`}
													onPointerDown={(event) => event.preventDefault()}
													onClick={(event) => {
														event.stopPropagation();
														requestDelete(item);
													}}
												>
													<Trash2 aria-hidden="true" />
												</Button>
											) : null}
										</CommandItem>
									);
								})}
								{filtered.length === 0 && !canCreate ? <CommandEmpty>No matches.</CommandEmpty> : null}
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
				</div>
			</div>

			<AlertDialog
				open={deleteDialog != null}
				onOpenChange={(next) => {
					if (!next && !deleting) setDeleteDialog(null);
				}}
			>
				<AlertDialogContent className="hub-inventory-lookup-alert">
					{deleteDialog?.kind === "confirm" ? (
						<>
							<AlertDialogHeader>
								<AlertDialogTitle>Delete {optionLabel}?</AlertDialogTitle>
								<AlertDialogDescription>
									{`Remove "${deleteDialog.name}" from the list. Existing assets are not changed.`}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
								<AlertDialogAction
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									disabled={deleting}
									onClick={(event) => {
										event.preventDefault();
										void confirmDelete();
									}}
								>
									Delete
								</AlertDialogAction>
							</AlertDialogFooter>
						</>
					) : null}
					{deleteDialog?.kind === "blocked" ? (
						<>
							<AlertDialogHeader>
								<AlertDialogTitle>Cannot delete {optionLabel}</AlertDialogTitle>
								<AlertDialogDescription>
									{`"${deleteDialog.name}" is used by ${deleteDialog.count} ${deleteDialog.count === 1 ? "asset" : "assets"}. Reassign or edit those assets first.`}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogAction onClick={() => setDeleteDialog(null)}>OK</AlertDialogAction>
							</AlertDialogFooter>
						</>
					) : null}
					{deleteDialog?.kind === "error" ? (
						<>
							<AlertDialogHeader>
								<AlertDialogTitle>Could not delete {optionLabel}</AlertDialogTitle>
								<AlertDialogDescription>{deleteDialog.message}</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogAction onClick={() => setDeleteDialog(null)}>OK</AlertDialogAction>
							</AlertDialogFooter>
						</>
					) : null}
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
